import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import get from 'lodash.get';
import uuid from 'uuid/v4';
import {
  Backdrop,
  Button,
  Stack,
  FlexBox,
  ButtonProps,
  AbsoluteBox,
  Box,
  Typography,
  MuiBox,
  color
} from '@kis-technologies/kis-ui';
import { ForwardNavigationButton } from 'components/ForwardNavigationButton';
import { localeMapping as momentLocaleMapping } from 'constants/moment';
import { selectors as configSelectors, Configuration } from 'services/configuration';
import { useStyles } from './styled';
import { CalendarOverlayProps, DateEntry } from '../types';

export const CalendarOverlay = (props: CalendarOverlayProps) => {
  const { title, confirmText, cancelText, onCancel, onConfirm } = props;

  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const fontFamily = get(config, 'configuration.globalSettings.fonts.primary', '');
  const classes = useStyles({ fontFamily })();

  const { t, i18n } = useTranslation();
  const localeStr = get(momentLocaleMapping, i18n.language);
  moment.locale(localeStr);

  const onEl = (el: HTMLElement) => {
    const { style } = el;
    style.background = '#EFEFEF';
  };

  const keyProps: ButtonProps = {
    appearance: 'white',
    customSize: 'giant',
    width: '120px',
    radius: 'subtle'
  };

  const selectedKeyProps: ButtonProps = {
    appearance: 'primary',
    customSize: 'giant',
    width: '120px',
    radius: 'subtle'
  };

  const disabledButton: React.CSSProperties = {
    borderWidth: '0.5px',
    borderColor: color.GRAY,
    color: color.LIGHT_GRAY,
    opacity: 0.25,
    borderStyle: 'solid'
  };
  const enabledButton: React.CSSProperties = {
    borderWidth: '0.5px',
    borderColor: color.GRAY,
    borderStyle: 'solid'
  };

  const [dateRangeStart, setDateRangeStart] = React.useState<Moment>(moment());
  const [dateRangeEnd, setDateRangeEnd] = React.useState<Moment>(moment());

  const [startDateFlag, setStartDateFlag] = React.useState<boolean>(false);
  const [endDateFlag, setEndDateFlag] = React.useState<boolean>(false);

  const [dateContext, setDateContext] = React.useState<Moment>(moment(new Date()));
  const [displayMonth, setDisplayMonth] = React.useState<string>(
    t('{{date, monthYear}}', { date: dateContext.toDate() })
  );

  const [viewDates, setViewDates] = React.useState<DateEntry[][]>([]);

  // These will be in the correct order according to locale
  const weekdays = moment.weekdays(true); // ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]

  React.useEffect(() => {
    getDatesForMonthView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateContext, displayMonth, dateRangeStart, dateRangeEnd]);

  const nextMonth = () => {
    const newMonth = dateContext.clone().add(1, 'month');
    setDisplayMonth(t('{{date, monthYear}}', { date: newMonth.toDate() }));
    setDateContext(newMonth);
  };

  const prevMonth = () => {
    setDateContext(dateContext.subtract(1, 'month'));
    setDisplayMonth(t('{{date, monthYear}}', { date: dateContext.toDate() }));
  };

  const disablePrevious = () => {
    if (dateContext.isSameOrBefore(moment(), 'month')) {
      return true;
    }

    return false;
  };

  const getDatesForMonthView = () => {
    let dayInMonth = dateContext.clone();
    const firstDayIdx = moment().startOf('week').isoWeekday() || 0;
    const lastDayIdx = moment().endOf('week').isoWeekday() || 6;
    const daysInMonth = dayInMonth.daysInMonth();
    dayInMonth = dateContext.clone();
    const firstIdx = dayInMonth.date(1).day() < firstDayIdx ? firstDayIdx - 7 : firstDayIdx % 7;
    dayInMonth = dateContext.clone();
    const firstDayInView = dayInMonth.date(1).day(firstIdx);
    dayInMonth = dateContext.clone();
    const lastIdx = dayInMonth.date(daysInMonth).day() > lastDayIdx ? lastDayIdx + 7 : lastDayIdx;
    dayInMonth = dateContext.clone();
    const lastDayInView = dayInMonth.date(daysInMonth).day(lastIdx);
    const now = firstDayInView.clone();
    const dates = [];
    let row = [];
    const dateAndIds = [];
    const firstDayInMonth = dateContext.clone().date(1);
    const lastDayInMonth = dateContext.clone().date(dateContext.daysInMonth());
    while (now.isSameOrBefore(lastDayInView)) {
      let tileStyleProps = keyProps;
      if (startDateFlag && endDateFlag) {
        if (
          now.startOf('day').isSameOrAfter(dateRangeStart.startOf('day')) &&
          now.startOf('day').isSameOrBefore(dateRangeEnd.startOf('day'))
        ) {
          tileStyleProps = selectedKeyProps;
        }
      } else if (startDateFlag) {
        if (now.startOf('day').isSame(dateRangeStart)) {
          tileStyleProps = selectedKeyProps;
        }
      } else if (endDateFlag) {
        if (now.startOf('day').isSame(dateRangeEnd)) {
          tileStyleProps = selectedKeyProps;
        }
      }

      row.push({
        displayDate: t('{{date, numericDay}}', { date: now.toDate() }),
        date: now.format(),
        day: now.day(),
        styleProps: tileStyleProps,
        disabled: Boolean(
          now.startOf('day').isBefore(firstDayInMonth.startOf('day')) ||
            now.startOf('day').isAfter(lastDayInMonth.startOf('day')) ||
            now.startOf('day').isBefore(moment().startOf('day'))
        )
      });

      if (row.length === 7) {
        dates.push(row);
        row = [];
        dateAndIds.push({
          dates,
          id: uuid()
        });
      }
      now.add(1, 'days');
    }

    setViewDates(dates);
  };

  const CurrentView = () => (
    <FlexBox direction="row" width={1000}>
      <FlexBox align="center" justify="between" fullWidth direction="row">
        {weekdays.map((day) => (
          <FlexBox key={day} direction="column" margin="0 2 2 2" width={120} align="center">
            <Typography fontSize={16}>{day}</Typography>
          </FlexBox>
        ))}
      </FlexBox>
      {viewDates.map((row, id) => (
        <FlexBox key={id} align="center" justify="between" direction="row" fullWidth>
          {row.map((tile) => (
            <FlexBox key={tile.date} direction="column" margin="2 2">
              {tile.disabled && (
                <Button value={tile.date} disabled={tile.disabled} {...tile.styleProps} sx={disabledButton}>
                  {tile.displayDate}
                </Button>
              )}
              {!tile.disabled && (
                <Button value={tile.date} {...tile.styleProps} sx={enabledButton} onClick={() => selectDate(tile.date)}>
                  {tile.displayDate}
                </Button>
              )}
            </FlexBox>
          ))}
        </FlexBox>
      ))}
    </FlexBox>
  );

  const selectDate = (selectedDate: string) => {
    const pickedDate = moment(selectedDate);
    if (startDateFlag && endDateFlag) {
      setDateRangeStart(pickedDate);
      setDateRangeEnd(pickedDate);
      setEndDateFlag(false);
    } else if (startDateFlag) {
      if (!pickedDate.startOf('day').isSame(dateRangeStart.startOf('day'))) {
        if (pickedDate.startOf('day').isBefore(dateRangeStart)) {
          setDateRangeEnd(dateRangeStart);
          setDateRangeStart(pickedDate);
        } else {
          setDateRangeEnd(pickedDate);
        }

        setEndDateFlag(true);
      }
    } else {
      setDateRangeStart(pickedDate);
      setDateRangeEnd(pickedDate);
      setStartDateFlag(true);
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(dateRangeStart, dateRangeEnd);
    }
  };

  return (
    <Backdrop onEl={onEl} open>
      <MuiBox className={classes.calendarContainer}>
        <MuiBox className={classes.calendarHeaderBox}>
          {/* Content */}
          <FlexBox direction="row" justify="center">
            <Typography className={classes.calenderTitle}>
              {title}
              {/* {description} */}
            </Typography>
          </FlexBox>

          <FlexBox direction="row" justify="between" padding="2 0 0 0">
            <FlexBox direction="column" flexOverride="none">
              <Button
                appearance="grayscale"
                onClick={() => prevMonth()}
                disabled={disablePrevious()}
                startIconName="chevron_left"
                width={130}
                className={classes.disabledPreviousButton}
              >
                {t('commonText.previous')}
              </Button>
            </FlexBox>

            <FlexBox direction="column" flexOverride="none">
              <Typography fontSize={30}>{displayMonth}</Typography>
            </FlexBox>
            <FlexBox direction="column" flexOverride="none">
              <Button appearance="grayscale" onClick={() => nextMonth()} endIconName="chevron_right" width={130}>
                {t('commonText.next')}
              </Button>
            </FlexBox>
          </FlexBox>
        </MuiBox>

        {/* Calendar */}
        <MuiBox className={classes.calendarCurrentView}>
          <CurrentView />
        </MuiBox>

        <Box fullWidth padding="5 10 0 10">
          <FlexBox fullWidth direction="row" justify="center">
            {!startDateFlag && !endDateFlag && (
              <Typography fontSize={25}>
                {/* {description} */}
                {t('calendar.selectDateRange')}
              </Typography>
            )}
            {(startDateFlag || endDateFlag) && (
              <Typography fontSize={25}>
                {/* {description} */}
                {`${t('calendar.dateRangeSelected')}${t('commonText.colon')} `}
                {t('{{date, monthDayYear}}', { date: dateRangeStart.toDate() })} {' - '}
                {t('{{date, monthDayYear}}', { date: dateRangeEnd.toDate() })}
              </Typography>
            )}
          </FlexBox>
        </Box>

        {/* Cancel and Confirm Buttons */}
        <MuiBox className={classes.calendarButtons}>
          <AbsoluteBox bottom="10px" left="623px">
            <FlexBox fullWidth>
              <Stack>
                {cancelText && (
                  <Button
                    customSize="large"
                    customColor={color.BLACK}
                    onClick={onCancel}
                    appearance="white"
                    width={332}
                  >
                    {cancelText}
                  </Button>
                )}
                {confirmText && (
                  <ForwardNavigationButton
                    isDisabled={!startDateFlag && !endDateFlag ? true : false}
                    label={confirmText}
                    onClick={handleConfirm}
                    size="large"
                    width={332}
                  />
                )}
              </Stack>
            </FlexBox>
          </AbsoluteBox>
        </MuiBox>
      </MuiBox>
    </Backdrop>
  );
};
